import React, { useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { createConsumer } from '@rails/actioncable';

const DEAL_TEAM_MESSAGES_QUERY = gql`
  query DealTeamMessages($id: ID!) {
    deal_team_messages(deal_team_id: $id)
  }
`;

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_e) {
    return '';
  }
}

const MessagesTab = ({ dealTeamId, conversationId, currentUserId, currentUserName }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const consumerRef = useRef(null);
  const subscriptionRef = useRef(null);
  const messagesRef = useRef([]);
  const messagesEndRef = useRef(null);

  // Hydrate persisted history on mount. Cable broadcasts append after this;
  // dedup by message id keeps the two paths consistent if a broadcast and
  // the query both deliver the same message.
  const { data: historyData } = useQuery(DEAL_TEAM_MESSAGES_QUERY, {
    variables: { id: dealTeamId },
    skip: !dealTeamId,
    fetchPolicy: 'cache-and-network',
  });
  useEffect(() => {
    const history = historyData?.deal_team_messages;
    if (!history) return;
    const normalized = history.map((m) => ({
      id: String(m.id),
      body: m.body,
      senderId: String(m.sender_user_id || ''),
      senderName: m.sender_name || 'Unknown',
      sentAt: m.sent_at,
    }));
    setMessages((prev) => {
      // Merge: keep any already-arrived cable messages not in history.
      const byId = new Map();
      [...normalized, ...prev].forEach((m) => byId.set(m.id, m));
      return Array.from(byId.values());
    });
  }, [historyData]);

  // Keep messagesRef in sync with messages state for use inside ActionCable callbacks
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!conversationId) return undefined;

    // Don't blow away history loaded from the GraphQL query — the dedup in
    // `received` keeps us consistent if a broadcast arrives that's also in
    // history.
    consumerRef.current = createConsumer();
    subscriptionRef.current = consumerRef.current.subscriptions.create(
      { channel: 'ChatChannel', room_id: conversationId },
      {
        connected: () => setIsConnected(true),
        disconnected: () => setIsConnected(false),
        rejected: () => setIsConnected(false),
        received: (data) => {
          if (!data) return;
          // V1: only append message events; persisted history reload deferred (note in spec).
          if (data.type === 'message' && data.message) {
            const incoming = {
              id: data.message.id,
              body: data.message.content,
              senderId: String(data.message.sender?.id || ''),
              senderName: data.message.sender?.name || data.message.sender?.user_name || 'Unknown',
              sentAt: data.message.sent,
            };
            if (messagesRef.current.some((m) => m.id === incoming.id)) return;
            setMessages((prev) => [...prev, incoming]);
          }
        },
        speak(payload) {
          return this.perform('speak', { room_id: conversationId, message: payload.message });
        },
      },
    );

    return () => {
      if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
      if (consumerRef.current) consumerRef.current.disconnect();
      subscriptionRef.current = null;
      consumerRef.current = null;
    };
  }, [conversationId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !subscriptionRef.current) return;

    subscriptionRef.current.speak({ message: trimmed });

    if (window.ahoy) {
      window.ahoy.track('succeedo_deal_team_message_sent', {
        conversation_id: conversationId,
      });
    }

    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Empty state if no conversation_id (no member has accepted yet)
  if (!conversationId) {
    return (
      <div className="max-w-2xl">
        <h2 className="font-serif text-[24px] font-light text-[hsl(var(--foreground))] mb-4">
          Team Messages
        </h2>
        <p className="text-[hsl(var(--muted-foreground))] text-[14px]">
          Group chat will start once a team member accepts their invitation.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-[24px] font-light text-[hsl(var(--foreground))]">
          Team Messages
        </h2>
        <span
          className={`font-mono text-[10px] uppercase tracking-wide ${
            isConnected
              ? 'text-emerald-500'
              : 'text-[hsl(var(--muted-foreground))]/60'
          }`}
        >
          {isConnected ? 'Connected' : 'Connecting…'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 mb-4 min-h-[300px] max-h-[60vh] pr-1">
        {messages.length === 0 ? (
          <p className="text-[hsl(var(--muted-foreground))] text-[14px]">
            Start a conversation with your team members.
          </p>
        ) : (
          messages.map((msg) => {
            const isMine = String(msg.senderId) === String(currentUserId);
            return (
              <div
                key={msg.id}
                className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}
              >
                {!isMine && (
                  <p className="text-[11px] font-medium text-[hsl(var(--muted-foreground))] mb-1 px-1">
                    {msg.senderName}
                  </p>
                )}
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                    isMine
                      ? 'bg-[hsl(var(--succeedo-orange))] text-white'
                      : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]'
                  }`}
                >
                  <p className="text-[14px] whitespace-pre-wrap break-words">
                    {msg.body}
                  </p>
                  <p className={`text-[10px] mt-1 ${isMine ? 'text-white/70' : 'text-[hsl(var(--muted-foreground))]'}`}>
                    {formatTimestamp(msg.sentAt)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2 border-t border-[hsl(var(--border))] pt-4">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message…"
          className="flex-1 px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] text-[14px] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/40"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!input.trim() || !isConnected}
          className="px-4 py-2 rounded-md bg-[hsl(var(--succeedo-orange))] text-white text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default MessagesTab;
